import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

// Definitions

export const fetchCustomFields = createAsyncThunk(
    'customFields/fetch',
    async (boardId, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/boards/${boardId}/custom-fields`, {
                method: 'GET', credentials: 'include', headers: jsonHeaders
            });
            if (res.status === 404) return { boardId, fields: [], disabled: true };
            if (!res.ok) throw new Error('Failed to load custom fields');
            const data = await res.json();
            return { boardId, fields: data.fields || [] };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createCustomField = createAsyncThunk(
    'customFields/create',
    async ({ boardId, field }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/boards/${boardId}/custom-fields`, {
                method: 'POST', credentials: 'include', headers: jsonHeaders, body: JSON.stringify(field)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to create custom field');
            return { boardId, field: data.field };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateCustomField = createAsyncThunk(
    'customFields/update',
    async ({ boardId, fieldId, patch }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/boards/${boardId}/custom-fields/${fieldId}`, {
                method: 'PATCH', credentials: 'include', headers: jsonHeaders, body: JSON.stringify(patch)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to update custom field');
            return { boardId, field: data.field, removedOptionIds: data.removedOptionIds || [] };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteCustomField = createAsyncThunk(
    'customFields/delete',
    async ({ boardId, fieldId }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/boards/${boardId}/custom-fields/${fieldId}`, {
                method: 'DELETE', credentials: 'include', headers: jsonHeaders
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to delete custom field');
            }
            return { boardId, fieldId };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const reorderCustomFields = createAsyncThunk(
    'customFields/reorder',
    async ({ boardId, orderedIds }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/boards/${boardId}/custom-fields/reorder`, {
                method: 'POST', credentials: 'include', headers: jsonHeaders, body: JSON.stringify({ orderedIds })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to reorder');
            return { boardId, orderedIds: data.orderedIds };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

// Values

export const setCardCustomFieldValue = createAsyncThunk(
    'customFields/setValue',
    async ({ cardId, fieldId, value }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/cards/${cardId}/custom-fields/${fieldId}`, {
                method: 'PUT', credentials: 'include', headers: jsonHeaders, body: JSON.stringify({ value })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to set value');
            return { cardId, fieldId, value: data.value };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const clearCardCustomFieldValue = createAsyncThunk(
    'customFields/clearValue',
    async ({ cardId, fieldId }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/cards/${cardId}/custom-fields/${fieldId}`, {
                method: 'DELETE', credentials: 'include', headers: jsonHeaders
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to clear value');
            }
            return { cardId, fieldId };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const sortByOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

const setFieldValueOnCard = (state, cardId, fieldId, value) => {
    const values = state.valuesByCard[cardId] || [];
    const idx = values.findIndex(v => v.fieldId === fieldId);
    if (value == null) {
        if (idx >= 0) values.splice(idx, 1);
    } else if (idx >= 0) {
        values[idx] = { ...value, fieldId };
    } else {
        values.push({ ...value, fieldId });
    }
    state.valuesByCard[cardId] = values;
};

const customFieldsSlice = createSlice({
    name: 'customFields',
    initialState: {
        // Definitions keyed by board shortId.
        fieldsByBoard: {},
        // Values keyed by card shortId. Each entry is the full subdoc from the API.
        valuesByCard: {},
        // Whether the backend reports the feature as enabled for the current board.
        enabledByBoard: {},
        status: 'idle',
        error: null
    },
    reducers: {
        // Bulk-load values for a card (called when a card detail is opened and
        // its `customFieldValues` are already part of the card payload).
        hydrateCardValues: (state, action) => {
            const { cardId, values } = action.payload;
            state.valuesByCard[cardId] = Array.isArray(values) ? values : [];
        },

        // Socket-driven reducers.
        socketFieldDefined: (state, action) => {
            const { boardId, field } = action.payload;
            const list = state.fieldsByBoard[boardId] || [];
            if (!list.some(f => f._id === field._id)) {
                list.push(field);
                list.sort(sortByOrder);
                state.fieldsByBoard[boardId] = list;
            }
        },
        socketFieldUpdated: (state, action) => {
            const { boardId, field, removedOptionIds } = action.payload;
            const list = state.fieldsByBoard[boardId] || [];
            const idx = list.findIndex(f => f._id === field._id);
            if (idx >= 0) list[idx] = field;
            else { list.push(field); list.sort(sortByOrder); }
            state.fieldsByBoard[boardId] = list;

            if (removedOptionIds?.length) {
                Object.keys(state.valuesByCard).forEach(cardId => {
                    state.valuesByCard[cardId] = state.valuesByCard[cardId].filter(v =>
                        !(v.fieldId === field._id && removedOptionIds.includes(v.optionId))
                    );
                });
            }
        },
        socketFieldDeleted: (state, action) => {
            const { boardId, fieldId } = action.payload;
            if (state.fieldsByBoard[boardId]) {
                state.fieldsByBoard[boardId] = state.fieldsByBoard[boardId].filter(f => f._id !== fieldId);
            }
            Object.keys(state.valuesByCard).forEach(cardId => {
                state.valuesByCard[cardId] = state.valuesByCard[cardId].filter(v => v.fieldId !== fieldId);
            });
        },
        socketFieldsReordered: (state, action) => {
            const { boardId, orderedIds } = action.payload;
            const list = state.fieldsByBoard[boardId] || [];
            const byId = new Map(list.map(f => [f._id, f]));
            state.fieldsByBoard[boardId] = orderedIds
                .map((id, idx) => {
                    const f = byId.get(id);
                    if (!f) return null;
                    return { ...f, order: idx };
                })
                .filter(Boolean);
        },
        socketCardValueUpdated: (state, action) => {
            const { cardId, fieldId, value } = action.payload;
            setFieldValueOnCard(state, cardId, fieldId, value);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomFields.fulfilled, (state, action) => {
                const { boardId, fields, disabled } = action.payload;
                state.fieldsByBoard[boardId] = [...fields].sort(sortByOrder);
                state.enabledByBoard[boardId] = !disabled;
            })
            .addCase(createCustomField.fulfilled, (state, action) => {
                const { boardId, field } = action.payload;
                const list = state.fieldsByBoard[boardId] || [];
                list.push(field);
                state.fieldsByBoard[boardId] = list.sort(sortByOrder);
            })
            .addCase(updateCustomField.fulfilled, (state, action) => {
                const { boardId, field, removedOptionIds } = action.payload;
                const list = state.fieldsByBoard[boardId] || [];
                const idx = list.findIndex(f => f._id === field._id);
                if (idx >= 0) list[idx] = field;
                state.fieldsByBoard[boardId] = list;

                if (removedOptionIds?.length) {
                    Object.keys(state.valuesByCard).forEach(cardId => {
                        state.valuesByCard[cardId] = state.valuesByCard[cardId].filter(v =>
                            !(v.fieldId === field._id && removedOptionIds.includes(v.optionId))
                        );
                    });
                }
            })
            .addCase(deleteCustomField.fulfilled, (state, action) => {
                const { boardId, fieldId } = action.payload;
                if (state.fieldsByBoard[boardId]) {
                    state.fieldsByBoard[boardId] = state.fieldsByBoard[boardId].filter(f => f._id !== fieldId);
                }
                Object.keys(state.valuesByCard).forEach(cardId => {
                    state.valuesByCard[cardId] = state.valuesByCard[cardId].filter(v => v.fieldId !== fieldId);
                });
            })
            .addCase(reorderCustomFields.fulfilled, (state, action) => {
                const { boardId, orderedIds } = action.payload;
                const list = state.fieldsByBoard[boardId] || [];
                const byId = new Map(list.map(f => [f._id, f]));
                state.fieldsByBoard[boardId] = orderedIds
                    .map((id, idx) => {
                        const f = byId.get(id);
                        return f ? { ...f, order: idx } : null;
                    })
                    .filter(Boolean);
            })
            .addCase(setCardCustomFieldValue.fulfilled, (state, action) => {
                const { cardId, fieldId, value } = action.payload;
                setFieldValueOnCard(state, cardId, fieldId, value);
            })
            .addCase(clearCardCustomFieldValue.fulfilled, (state, action) => {
                const { cardId, fieldId } = action.payload;
                setFieldValueOnCard(state, cardId, fieldId, null);
            });
    }
});

export const {
    hydrateCardValues,
    socketFieldDefined,
    socketFieldUpdated,
    socketFieldDeleted,
    socketFieldsReordered,
    socketCardValueUpdated
} = customFieldsSlice.actions;

// Stable empty-array reference. Returning a fresh `[]` literal from a selector
// when there's no data makes useSelector see a new reference every call, which
// triggers React's "selector returned a different result with the same
// parameters" warning and causes needless re-renders. Reuse one frozen array.
const EMPTY_FIELDS = Object.freeze([]);

export const selectBoardCustomFields = (state, boardId) =>
    state.customFields.fieldsByBoard[boardId] || EMPTY_FIELDS;

export const selectCardCustomFieldValues = (state, cardId) =>
    state.customFields.valuesByCard[cardId] || EMPTY_FIELDS;

export default customFieldsSlice.reducer;
